import { ALIGNMENT_ENUM, CoreDivider, CoreLayoutView, CoreTextView, ORIENTATION_ENUM } from '@core/widgets';
import { ConditionalRender, debounce, numberFormat, useBehavior, useButton, useTriggerAction, useUpdateWidget, useUpdateWidgetValue } from '@libs/core';
import { TCalculatorBonus, TCalculatorData, TCommonDataObj, TWidget, TWidgetProps } from '@libs/types';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDetectKeyboard } from '../../hooks/useDetectKeyboard';
import { TInputOption } from '../../types/options';
import { TextStyle } from '../../types/styles';
import { getInlineStyles } from '../../utils/styles';
import { PopupComponent } from '../UI/Popup/PopupComponent';
import styles from './Calculator.module.css';
import { CalculatorSubmitButton } from './CalculatorSubmitButton';
import { CodeAppliedButtons } from './code/CodeAppliedButtons';
import { CodeButton } from './code/CodeButton';
import { CodeInput } from './code/CodeInput';
import { Bonus } from './fee/Bonus';
import { Fee } from './fee/Fee';
import { FeeBlock } from './fee/FeeBlock';
import { CalculatorInput } from './input/CalculatorInput';
import { CalculatorInputSubRow } from './input/CalculatorInputSubRow';
import { useFontSize } from './utils/fontSizeService';
import { toFixedSum } from './utils/format/format';
import { ActionNameEnum, CurrencyDropdownEnum, InputEnum, TErrorsResult, TResultServerCalculationFunc } from './utils/types';
import { getCurrency, getDataValueFromWidget, getErrors, getListsCurrency, getWaitFor, isCanDoRequest } from './utils/widgets';

const Calculator = ({ widget }: TWidgetProps<TCalculatorData>) => {
    'use no memo';
    const { keyboardHeight, isKeyboardOpen } = useDetectKeyboard();
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const { updateWidgetValueByKey } = useUpdateWidgetValue(widget);
    const [isLoading, setLoading] = useState(false);
    const uid = widget.uid;
    const tabIndex = widget.data?.tabIndex ?? 0;
    const { mutableWidget: initialWidget } = useUpdateWidget(uid);
    const isFrom = ((initialWidget.data.value as TCommonDataObj)['isFrom'] as boolean) ?? false;
    const { triggerAction, triggerActionsByType, triggerActionsByNameAndType } = useTriggerAction(
        initialWidget as unknown as TWidget<TCalculatorData, TInputOption>,
    );
    const { isDisabled } = useBehavior(initialWidget as TWidget<TCalculatorData>);
    const { handleClickByActionName } = useButton<TCalculatorData>(initialWidget as TWidget<TCalculatorData>, {
        isDisabled,
        triggerActionsByType,
        triggerActionsByNameAndType,
    });
    const options = widget.options ?? {};
    const parentStyleNames = [options.style].filter(Boolean) as string[];
    const initialValue = initialWidget?.data?.value as TCommonDataObj;
    const { fromInputValue, toInputValue, fromCurrencyValue, toCurrencyValue, codeValue } = getDataValueFromWidget(initialValue as TCommonDataObj);
    const { fontSize, updateFontSize } = useFontSize(fromInputValue, toInputValue);
    const [currentWidget, setCurrentWidget] = useState(initialWidget as TWidget<TCalculatorData>);
    const calculatorData = currentWidget?.data ?? {};
    const messages = calculatorData?.messages ?? {};
    const fee = calculatorData?.fee ?? {};
    const bonus = calculatorData?.bonus;
    const rate = calculatorData?.rate ?? {};
    const code = calculatorData?.code ?? {};
    const { fromCurrencyList, toCurrencyList } = useMemo(() => getListsCurrency(calculatorData.listCurrency), [calculatorData.listCurrency]);
    const fromCurrency = useMemo(() => getCurrency(fromCurrencyValue, fromCurrencyList), [fromCurrencyValue, fromCurrencyList]);
    const toCurrency = useMemo(() => getCurrency(toCurrencyValue, toCurrencyList), [toCurrencyValue, toCurrencyList]);
    const currentErrors = useMemo(() => getErrors(calculatorData?.errors ?? []), [calculatorData?.errors]);
    const waitFor = useMemo(() => getWaitFor(currentWidget?.actions), [currentWidget?.actions]);
    const isShowInfoSection = Boolean(messages.feeLabel?.trim() || messages.rateLabel?.trim() || bonus);
    const isShowCodeSection = Boolean(messages.codeButtonLabel?.trim());
    const isShowSubmitButton = Boolean(messages.buttonLabelMain?.trim());
    const hasErrorFromInput = currentErrors.fromInput && Object.keys(currentErrors.fromInput).length > 0;
    const hasErrorToInput = currentErrors.toInput && Object.keys(currentErrors.toInput).length > 0;
    const isEnabled = Boolean(
        +fromInputValue &&
        +toInputValue &&
        !fromInputValue.endsWith('.') &&
        !toInputValue.endsWith('.') &&
        Boolean(currentWidget?.data?.buttonContinueEnabled) &&
        !isLoading,
    );

    const onServerCalculation = useCallback(
        async (
            value: string,
            name: string,
            isFrom?: boolean,
            actionName: ActionNameEnum = ActionNameEnum.onCalculate,
        ): Promise<TResultServerCalculationFunc | undefined> => {
            const action = widget.actions?.find((a) => a?.name === actionName);
            if (isCanDoRequest(value, action) && action) {
                setLoading(true);
                try {
                    updateWidgetValueByKey(value, name);
                    if (isFrom !== null && isFrom !== undefined) {
                        updateWidgetValueByKey(isFrom, 'isFrom');
                    }
                    const result =
                        actionName === ActionNameEnum.onCalculate
                            ? await triggerActionsByType(ActionNameEnum.onCalculate, { isUpdateWidgetByUid: false })
                            : await triggerAction(action, { isUpdateWidgetByUid: true });
                    setLoading(false);
                    if (result) {
                        const resultValue = (result?.data?.value as TCommonDataObj) ?? {};
                        if (name === InputEnum.FROM_INPUT || name === InputEnum.TO_INPUT) {
                            const oppositeName = isFrom ? InputEnum.TO_INPUT : InputEnum.FROM_INPUT;
                            const resultFromValue = (resultValue?.fromInput as string) ?? fromInputValue;
                            const resultToValue = (resultValue?.toInput as string) ?? toInputValue;
                            const oppositeValue = isFrom ? resultToValue : resultFromValue;
                            updateFontSize(value, oppositeValue);
                            updateWidgetValueByKey(oppositeValue, oppositeName);
                        }
                        setCurrentWidget(result);
                        const errors = getErrors(result?.data?.errors ?? []);
                        return { value: result?.data?.value, errors };
                    }
                    return { value: {}, errors: {} };
                } catch (e) {
                    console.error(e);
                    return { value: {}, errors: {} };
                } finally {
                    setLoading(false);
                }
            }
        },
        [widget.actions, updateWidgetValueByKey, triggerActionsByType, triggerAction, fromInputValue, toInputValue, updateFontSize],
    );
    const debounceCalcCommission = debounce(timeoutRef, onServerCalculation, waitFor);

    const handleUpdateValue = useCallback(
        (clearValue: string, name: string, isFromFlag?: boolean) => {
            if (typeof isFromFlag === 'boolean') {
                updateWidgetValueByKey(isFromFlag, 'isFrom');
            }
            updateWidgetValueByKey(clearValue, name);
            const error = currentErrors[name as keyof TErrorsResult];
            if (error && Object.keys(error).length > 0) {
                setCurrentWidget((prevState) => {
                    const newErrors = prevState.data?.errors?.filter((e) => e.field !== error.field);
                    return {
                        ...prevState,
                        data: {
                            ...prevState.data,
                            errors: newErrors && newErrors.length > 0 ? newErrors : null,
                        },
                    };
                });
            }
            updateFontSize(clearValue, isFromFlag ? toInputValue : fromInputValue);
            debounceCalcCommission(clearValue, name, isFromFlag);
        },
        [currentErrors, debounceCalcCommission, fromInputValue, toInputValue, updateFontSize, updateWidgetValueByKey],
    );
    const handleButtonClick = useCallback(() => {
        handleClickByActionName(ActionNameEnum.submit);
    }, [handleClickByActionName]);

    const enterCodePopup = (
        <PopupComponent
            withoutSeparator
            header={
                <CodeInput
                    label={messages.codeButtonLabel}
                    labelGotIt={messages.feeBottomSheetButton}
                    text={messages?.codeTextEnter}
                    textLock={messages?.codeTextLock}
                    textAddedCode={messages.codeTextAddedCode}
                    onServerCalculation={onServerCalculation}
                    isFrom={isFrom}
                    parentStyleNames={parentStyleNames}
                />
            }
        />
    );

    const rateText = `${numberFormat(rate.from)} ${fromCurrencyValue ?? ''} = ${numberFormat(rate?.to, 4)} ${toCurrencyValue ?? ''}`;

    return (
        <div
            className={styles.calculatorFrame}
            data-test={widget.uid}
            style={getInlineStyles({
                variables: {
                    keyboardHeight: isKeyboardOpen ? `${keyboardHeight}px` : '0px',
                },
            })}
        >
            <div className={styles.calculatorFrameTop}>
                <CoreLayoutView
                    styleName="calculatorInputsRow"
                    parentStyleNames={parentStyleNames}
                    styleProps={{ orientation: ORIENTATION_ENUM.HORIZONTAL, gap: 16 }}
                >
                    <CalculatorInput
                        isFrom={true}
                        name={InputEnum.FROM_INPUT}
                        value={fromInputValue}
                        label={messages.fromInputLabel}
                        fontSize={fontSize}
                        isError={hasErrorFromInput}
                        handleUpdateValue={handleUpdateValue}
                        tabIndex={tabIndex}
                        parentStyleNames={parentStyleNames}
                        subRow={
                            <CalculatorInputSubRow
                                widget={widget}
                                isFrom={true}
                                inputName={InputEnum.FROM_INPUT}
                                dropdownName={CurrencyDropdownEnum.FROM_CURRENCY}
                                value={fromCurrencyValue}
                                newInputValue={currentErrors.fromInput?.newInputValue}
                                flagName={fromCurrency.currency?.flagName ?? ''}
                                index={fromCurrency.currencyIndex}
                                isSingleCurrency={fromCurrency.isSingleCurrency}
                                items={fromCurrencyList}
                                error={currentErrors.fromInput}
                                onServerCalculation={onServerCalculation}
                                handleUpdateValue={handleUpdateValue}
                                searchPlaceholder={messages.searchPlaceholder}
                                noFoundText={messages.noResultFoundText}
                                dropDownHeader={messages.listFromCurrencyText}
                                currencyText={messages.currencyListItemSubtitleText}
                                buttonGotItLabel={messages.buttonGotItLabel}
                                buttonIncreaseLimitLabel={messages.buttonIncreaseLimitLabel}
                                buttonChangeAmountLabel={messages.buttonChangeAmountLabel}
                                buttonChangeDeliveryOptionLabel={messages.buttonChangeDeliveryOptionLabel}
                                buttonChangeTransferOptionLabel={messages.buttonChangeTransferOptionLabel}
                                parentStyleNames={parentStyleNames}
                            />
                        }
                    />
                    <CalculatorInput
                        isFrom={false}
                        name={InputEnum.TO_INPUT}
                        value={toInputValue}
                        label={messages.toInputLabel}
                        fontSize={fontSize}
                        isError={hasErrorToInput}
                        handleUpdateValue={handleUpdateValue}
                        tabIndex={tabIndex + 1}
                        parentStyleNames={parentStyleNames}
                        subRow={
                            <CalculatorInputSubRow
                                widget={widget}
                                isFrom={false}
                                inputName={InputEnum.TO_INPUT}
                                dropdownName={CurrencyDropdownEnum.TO_CURRENCY}
                                value={toCurrencyValue}
                                newInputValue={currentErrors.toInput?.newInputValue}
                                flagName={toCurrency.currency?.flagName ?? ''}
                                index={toCurrency.currencyIndex}
                                isSingleCurrency={toCurrency.isSingleCurrency}
                                items={toCurrencyList}
                                error={currentErrors.toInput}
                                onServerCalculation={onServerCalculation}
                                handleUpdateValue={handleUpdateValue}
                                searchPlaceholder={messages.searchPlaceholder}
                                noFoundText={messages.noResultFoundText}
                                dropDownHeader={messages.listToCurrencyText}
                                currencyText={messages.currencyListItemSubtitleText}
                                buttonGotItLabel={messages.buttonGotItLabel}
                                buttonIncreaseLimitLabel={messages.buttonIncreaseLimitLabel}
                                buttonChangeAmountLabel={messages.buttonChangeAmountLabel}
                                buttonChangeDeliveryOptionLabel={messages.buttonChangeDeliveryOptionLabel}
                                buttonChangeTransferOptionLabel={messages.buttonChangeTransferOptionLabel}
                                parentStyleNames={parentStyleNames}
                            />
                        }
                    />
                </CoreLayoutView>
            </div>
            <ConditionalRender condition={isShowInfoSection}>
                <>
                    <CoreDivider
                        isWidget={false}
                        styleName="calculatorDivider"
                        parentStyleNames={parentStyleNames}
                        styleProps={{ outlineVariant: 'var(--grey_16_tr)', margin: { top: 24 } }}
                    />
                    <CoreLayoutView
                        styleName="calculatorInfoRow"
                        parentStyleNames={parentStyleNames}
                        styleProps={{ orientation: ORIENTATION_ENUM.HORIZONTAL, padding: { top: 24, right: 24, bottom: 24, left: 24 } }}
                    >
                        <Fee
                            bottomSheetButton={messages.feeBottomSheetButton}
                            feeBottomSheetFirstText={messages.feeBottomSheetFirstText}
                            feeBottomSheetSecondText={messages.feeBottomSheetSecondText}
                            feeOptions={messages.feeBottomSheetOptions}
                            sum={toFixedSum(fee.sum)}
                            currency={fee.currency}
                            iconName={fee.iconName}
                            label={messages.feeLabel ?? ''}
                            text={messages.feeText ?? ''}
                            parentStyleNames={parentStyleNames}
                        />
                        <FeeBlock title={messages.rateLabel} rate={rateText} parentStyleNames={parentStyleNames} />
                        <ConditionalRender condition={!!bonus}>
                            <Bonus
                                bonus={bonus as TCalculatorBonus}
                                label={messages.bonusLabel ?? ''}
                                parentStyleNames={parentStyleNames}
                                onClick={() => {
                                    triggerActionsByType(ActionNameEnum.bonusInfoBs).then();
                                }}
                            />
                        </ConditionalRender>
                    </CoreLayoutView>
                </>
            </ConditionalRender>
            <ConditionalRender condition={isShowCodeSection}>
                <CoreLayoutView styleProps={{ orientation: ORIENTATION_ENUM.BOX }}>
                    <CoreDivider
                        isWidget={false}
                        styleName="calculatorDivider"
                        parentStyleNames={parentStyleNames}
                        styleProps={{ outlineVariant: 'var(--grey_16_tr)', alignment: ALIGNMENT_ENUM.CENTER_LEFT }}
                    />
                    <CodeButton
                        value={codeValue}
                        label={messages.codeButtonLabel}
                        parentStyleNames={parentStyleNames}
                        handlingAutoApplied={code.handlingAutoApplied}
                        appliedCodePopup={
                            <PopupComponent
                                header={
                                    <CoreTextView
                                        text={code.handlingAutoApplied ? messages?.codeTextUse : messages?.codeTextApplied}
                                        styleName="calculatorBsText"
                                        parentStyleNames={parentStyleNames}
                                        styleProps={{ ...TextStyle.main, main: 'var(--black)', textAlign: 'center' }}
                                    />
                                }
                                externalContent={[
                                    <CodeAppliedButtons
                                        key="codeAppliedButtons"
                                        labelNewCode={messages?.codeLabelNewCode}
                                        labelRemoveCode={messages?.codeLabelRemoveCode}
                                        labelUsePromoCode={messages?.codeLabelUseCode}
                                        textRemovedCode={messages.codeTextRemovedCode}
                                        value={codeValue}
                                        handlingAutoApplied={code.handlingAutoApplied}
                                        enterCodePopup={enterCodePopup}
                                        onServerCalculation={onServerCalculation}
                                        isFrom={isFrom}
                                        parentStyleNames={parentStyleNames}
                                    />,
                                ]}
                            />
                        }
                        enterCodePopup={enterCodePopup}
                    />
                </CoreLayoutView>
            </ConditionalRender>
            <ConditionalRender condition={isShowSubmitButton}>
                <CalculatorSubmitButton
                    handleClick={handleButtonClick}
                    label={messages.buttonLabelMain}
                    isEnabled={isEnabled}
                    parentStyleNames={parentStyleNames}
                />
            </ConditionalRender>
        </div>
    );
};
export { Calculator };
